import React from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'motion/react';
import {
  FaArrowRight, FaCrosshairs, FaServer, FaCode, FaClipboardCheck,
} from 'react-icons/fa';
import PageNavbar from './PageNavbar';
import Footer from './Footer';
import { getRevealProps, staggerContainer, staggerItem, defaultViewport } from '../motion/scrollMotion';
import './ServiceDetailPage.css';

const practices = [
  {
    icon: <FaCrosshairs />,
    name: 'Offensive Security',
    desc: 'Penetration testing, red teaming, and vulnerability assessment. Led by CISSP, CEH, and OSCP certified testers — every finding is validated by hand, not a raw scanner export.',
  },
  {
    icon: <FaServer />,
    name: 'Managed IT & Security Operations',
    desc: '24/7 SOC monitoring, endpoint management, and backup/DR. Staffed by Certified SOC Analysts (CSA) — the team on call when an alert needs a human decision at 2 a.m.',
  },
  {
    icon: <FaCode />,
    name: 'Secure Software Development',
    desc: 'Custom applications, AI development, and Secure SDLC integration. Engineers who build with security requirements from the first sprint, not bolted on before release.',
  },
  {
    icon: <FaClipboardCheck />,
    name: 'Compliance & Advisory',
    desc: 'ISO 27001, NIST CSF 2.0, CIS Controls, NIST AI RMF, and regional frameworks (DPDP, UK GDPR, RBI, NCSC CAF). Led by our ISO 27001 Lead Auditors and CISA holders who have prepared organizations for real certification audits, not just paperwork.',
  },
];

const LeadershipPage = () => (
  <div className="svc-page">
    <PageNavbar />

    <section className="svc-hero">
      <div className="svc-hero-glow" />
      <div className="svc-container">
        <motion.div className="svc-hero-text" style={{ maxWidth: '46rem' }} {...getRevealProps('fadeUp')}>
          <span className="svc-eyebrow">Leadership & Expertise</span>
          <h1 className="svc-hero-title">How Our Team Is Structured.</h1>
          <p className="svc-hero-desc">
            We are not publishing individual staff bios on this page — client engagements, not
            personal branding, are where our team's judgment actually shows up. What we can tell
            you is how the practice is organized, who leads which discipline, and what
            certifications sit behind the work.
          </p>
          <div className="svc-hero-actions">
            <Link to="/contact" className="svc-btn svc-btn-primary">
              Get In Touch <FaArrowRight />
            </Link>
            <Link to="/certifications" className="svc-btn svc-btn-ghost">
              See Our Certifications
            </Link>
          </div>
        </motion.div>
      </div>
    </section>

    <section className="svc-section">
      <div className="svc-container">
        <header className="svc-section-head">
          <span className="svc-section-tag">Four Practices, One Team</span>
          <h2 className="svc-section-title">Organized Around Outcomes, Not Org Charts</h2>
          <p className="svc-section-sub">
            Cybersecurity, managed IT, and secure development report into one leadership structure
            deliberately — so a finding from a pentest, a monitoring alert, and a code review all
            reach the same table.
          </p>
        </header>

        <motion.div
          className="svc-feature-grid"
          initial="hidden"
          whileInView="visible"
          viewport={defaultViewport}
          variants={{ ...staggerContainer, visible: { transition: { staggerChildren: 0.08 } } }}
        >
          {practices.map((p) => (
            <motion.article className="svc-feature-card" key={p.name} variants={staggerItem}>
              <div className="svc-feature-icon">{p.icon}</div>
              <h3 className="svc-feature-title">{p.name}</h3>
              <p className="svc-feature-desc">{p.desc}</p>
            </motion.article>
          ))}
        </motion.div>
      </div>
    </section>

    <section className="svc-section svc-section-alt">
      <div className="svc-container svc-why-grid">
        <motion.div className="svc-why-text" {...getRevealProps('slideLeft')}>
          <span className="svc-section-tag">Why It Works This Way</span>
          <h2 className="svc-section-title">One Escalation Path, Not Four Vendors</h2>
          <p className="svc-why-body">
            Most mid-market organizations end up with a pentest vendor, an MSP, and a dev shop who
            have never spoken to each other — and a finding from one rarely reaches the others.
            We run all three under one leadership structure so a vulnerability found in testing,
            a pattern seen in SOC monitoring, and a design decision in development inform each
            other instead of sitting in three separate inboxes.
          </p>
        </motion.div>

        <motion.ul className="svc-why-list" {...getRevealProps('slideRight', 0.12)}>
          <li><span className="svc-why-check">✓</span> Cybersecurity findings feed directly into managed IT remediation</li>
          <li><span className="svc-why-check">✓</span> Compliance requirements shape how we build and configure, not just how we audit</li>
          <li><span className="svc-why-check">✓</span> One point of accountability across every engagement, not three</li>
          <li><span className="svc-why-check">✓</span> Certified leads in every practice — see our <Link to="/certifications" style={{ color: '#FF8A00' }}>certifications page</Link></li>
        </motion.ul>
      </div>
    </section>

    <section className="svc-cta">
      <div className="svc-container">
        <motion.div className="svc-cta-card" {...getRevealProps('scaleUp')}>
          <h2 className="svc-cta-title">Want to talk to the team directly?</h2>
          <p className="svc-cta-desc">
            We respond to every inquiry within one business day.
          </p>
          <div className="svc-cta-actions">
            <Link to="/contact" className="svc-btn svc-btn-primary">
              Book Consultation <FaArrowRight />
            </Link>
            <Link to="/about" className="svc-btn svc-btn-ghost-light">
              About Proven InfoSec
            </Link>
          </div>
        </motion.div>
      </div>
    </section>

    <Footer />
  </div>
);

export default LeadershipPage;
